<script setup lang="ts">
    import { ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import { fileIconForMime } from 'models'
    import {
        useChatbotStore,
        useDialogConfirm,
        useFileRow,
        useUserFileKind,
        type ChatbotFile,
    } from 'composables'

    const props = defineProps<{ agentId: string }>()

    const { t: $t } = useI18n({ useScope: 'global' })
    const { openDialogConfirm } = useDialogConfirm()

    const store = useChatbotStore(props.agentId)
    const { chatFiles, isLoadingChatFiles } = storeToRefs(store)
    const { showThumb, thumbUrl, onThumbError, openFile, formatDate } =
        useFileRow({
            view: store.getChatFileUrl,
            preview: store.getChatFilePreviewUrl,
        })
    const { kindNote, removeLabel, removeConfirm } = useUserFileKind()

    const deletingId = ref<string | null>(null)

    const handleExclude = async (file: ChatbotFile) => {
        const proceed = await openDialogConfirm(removeConfirm(file))
        if (!proceed) {
            return
        }
        deletingId.value = file.documentId
        try {
            await store.deleteChatFile(file.documentId)
        } catch {
            // surfaced by the host's error handling
        } finally {
            deletingId.value = null
        }
    }
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 relative">
        <!-- Loading skeleton, shaped like the rows it replaces -->
        <div
            v-if="isLoadingChatFiles && chatFiles.length === 0"
            class="vv-item-group vv-item-group--fill">
            <div class="vv-item-group__list">
                <div
                    v-for="i in 4"
                    :key="i"
                    class="vv-item vv-item--roomy vv-item--plain">
                    <div class="vv-item__entry">
                        <div class="vv-skeleton w-36 h-36 shrink-0 rounded-md">
                            <div class="vv-skeleton__item" />
                        </div>
                        <div class="flex flex-col gap-6 flex-1 min-w-0">
                            <div
                                class="vv-skeleton h-14 rounded"
                                :class="i % 2 ? 'w-192' : 'w-128'">
                                <div class="vv-skeleton__item" />
                            </div>
                            <div class="vv-skeleton h-12 rounded w-96">
                                <div class="vv-skeleton__item" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div
            v-else-if="chatFiles.length === 0"
            class="flex flex-col flex-1 items-center justify-center gap-12 p-24 text-center">
            <div
                class="w-64 h-64 rounded-full bg-surface-1 border border-surface-3 flex items-center justify-center">
                <VvIcon
                    name="ri:folder-open-line"
                    class="w-28 h-28 text-word-4" />
            </div>
            <span class="text-14 text-word-3 leading-relaxed">
                {{ $t('userFiles.chatFilesEmpty') }}
            </span>
        </div>

        <!-- File list -->
        <div v-else class="vv-item-group vv-item-group--fill">
            <ul
                class="pk-chatbot-files vv-item-group__list overflow-y-auto light-scrollbar">
                <li
                    v-for="file in chatFiles"
                    :key="file.documentId"
                    class="vv-item vv-item--roomy">
                    <button
                        type="button"
                        class="vv-item__entry"
                        :title="$t('userFiles.open')"
                        @click="openFile(file)">
                        <span class="pk-chatbot-files__icon">
                            <img
                                v-if="showThumb(file)"
                                :src="thumbUrl(file)"
                                loading="lazy"
                                decoding="async"
                                alt=""
                                class="pk-chatbot-files__thumb"
                                @error="onThumbError(file)" />
                            <VvIcon
                                v-else
                                :name="fileIconForMime(file.mimeType)" />
                        </span>
                        <span class="flex flex-col min-w-0 flex-1">
                            <span class="vv-item__title">
                                {{ file.name || $t('userFiles.untitled') }}
                            </span>
                            <span class="text-12 text-word-3">
                                {{
                                    [formatDate(file.createdAt), kindNote(file)]
                                        .filter(Boolean)
                                        .join(' · ')
                                }}
                            </span>
                        </span>
                    </button>
                    <div class="vv-item__actions pk-chatbot-files__actions">
                        <VvButton
                            modifiers="action-quiet-small"
                            icon="ri:download-line"
                            :title="$t('action.download')"
                            @click="openFile(file, true)" />
                        <VvButton
                            modifiers="action-quiet-small"
                            icon="ri:delete-bin-line"
                            :title="removeLabel(file)"
                            :loading="deletingId === file.documentId"
                            :disabled="deletingId === file.documentId"
                            @click="handleExclude(file)" />
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<style lang="scss">
    // The list and its rows come from `vv-item-group` and `vv-item`; what is
    // left here is the file tile and the pair of row actions.
    .pk-chatbot-files {
        // The box exists to clip a thumbnail and to keep the labels of rows
        // with and without one on the same left edge: with a file icon in it
        // there is nothing to draw, so it stays transparent.
        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-36);
            height: var(--spacing-36);
            flex-shrink: 0;
            border-radius: var(--rounded-md);
            color: var(--color-word-3);
            overflow: hidden;

            svg {
                width: var(--spacing-20);
                height: var(--spacing-20);
            }
        }

        &__thumb {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &__actions {
            gap: var(--spacing-2);
        }
    }
</style>
